import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { EventModel, EventOptionGroup, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { MarqueeConfig } from '@frontend/sports/common/client-config-data-access';
import { MarketType } from '@frontend/sports/types/components/content';
import { MapMarketToGameTemplateIds } from '@frontend/sports/types/components/highlights-marquee';

import { OptionGroupService } from '../event-model/helpers/option-group.service';
import { FixtureTile } from './marquee-tile.model';

@Injectable({ providedIn: 'root' })
export class MarqueeHelperService {
    gridGroupId: string;
    tile: FixtureTile;
    fallbackFlagCollection: { [key: string]: boolean } = {};

    constructor(
        private marqueeConfig: MarqueeConfig,
        private optionGroupService: OptionGroupService,
        private shortTeamNamesService: ShortTeamNamesService,
    ) {}

    getParticipantName(participantIndx: number, event: EventModel): string {
        const participants = event.participants;
        const participant = participants[participantIndx];

        if (this.shortTeamNamesService.shouldUseShortName(participant, event.sport.id, event.league.id, ShortNameModule.Marquee)) {
            const name = participant.shortName!;

            return name.replace(/-/g, '\u2011');
        }

        // Replacing with non breaking dash, to overcome issues of line breaking
        return participant.name.replace(/-/g, '\u2011');
    }

    getOptionGroup(tile: FixtureTile): EventOptionGroup | undefined {
        let eventOptions: EventOptionGroup | undefined;
        this.fallbackFlagCollection[tile.fixtureId + tile.marketType] = false;
        this.tile = tile;
        this.removeGridGroupIdsIfEmpty(this.tile);
        if (
            this.tile.gridGroupIds?.length === 1 &&
            (this.tile.gameTemplateIds?.length || this.tile.source?.optionMarketParameters?.marketTypes.length)
        ) {
            return this.getOptionGroupsForGridableAndNonGridableMarkets(eventOptions);
        }
        if (this.tile.gridGroupIds?.length === 2 && !this.tile.gameTemplateIds?.length) {
            return this.getOptionGroupsForGridableMarkets(eventOptions);
        }
        if (this.tile.gameTemplateIds.length && !this.tile.gridGroupIds?.length) {
            return this.getOptionGroupsForTv1NonGridableMarkets(eventOptions);
        }
        if (this.tile.source?.optionMarketParameters?.marketTypes.length && !this.tile.gameTemplateIds?.length && !this.tile.gridGroupIds?.length) {
            return this.getOptionGroupsForTv2NonGridableMarkets(eventOptions);
        }
        if (this.tile.gridGroupIds?.length === 3 || this.tile.gridGroupIds?.length === 4) {
            return this.getSixpackOptionGroups();
        }
        //Marquee with gridable market and without fallback market
        if (this.tile.gridGroupIds?.length === 1 && this.tile.fallbackMarketType === MarketType.Unknown) {
            return this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[0]);
        }
        //Marquee with Matchwinner market without fallback
        if (
            !this.tile.gridGroupIds?.length &&
            this.tile.marketType === MarketType.MatchWinner &&
            this.tile.fallbackMarketType === MarketType.Unknown
        ) {
            return this.tile.event.optionGroups.find((optionGroup) => optionGroup.winning && !optionGroup.hidden);
        }
        //Marquee with gridable market and with fallback market
        if (this.tile.gridGroupIds?.length === 1 && this.tile.fallbackMarketType !== MarketType.Unknown) {
            return this.getOptionGroupsForGridableAndNonGridableMarkets(eventOptions);
        }
        //Marquee with non-gridable market and without fallback market - Tv1
        if (this.tile.gameTemplateIds?.length) {
            return this.getGridableAndNonGridableOptionGroup(false);
        }
        //Marquee with non-gridable market and without fallback market -Tv2
        if (this.tile.source?.optionMarketParameters?.marketTypes.length) {
            const nonGridableMarketType = this.marqueeConfig.mapMarketToGameTemplateIds.find(
                (i) => i.sportId === this.tile.sportId && i.market === this.tile.marketType,
            );
            const marketType = nonGridableMarketType?.marketType;

            return this.getTv2NonGridableOptionGroup(marketType!);
        }

        return this.tile.optionGroup;
    }

    private getGridableAndNonGridableOptionGroup(isGridable: boolean, gridGroupId?: string): EventOptionGroup | undefined {
        if (isGridable) {
            this.gridGroupId = this.tile.gridGroupIds[0];

            return this.optionGroupService.get(this.tile.event.optionGroups, gridGroupId);
        } else {
            if (this.tile.event.offerSource === OfferSource.V1) {
                return this.tile.event.optionGroups.find((optionGroup) => this.tile.gameTemplateIds.some((gt) => gt === optionGroup.templateId));
            } else if (this.tile.event.offerSource === OfferSource.V2 && this.tile.source?.optionMarketParameters) {
                return this.tile.event.optionGroups.find((optionGroup) =>
                    this.tile.source?.optionMarketParameters!.marketTypes.some(
                        (gt) => gt === optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value,
                    ),
                );
            }
        }

        return;
    }

    private getTv1NonGridableOptionGroup(gameTemplateIds: string[]): EventOptionGroup | undefined {
        return this.tile.event.optionGroups.find((optionGroup) => gameTemplateIds.some((gt) => gt === optionGroup.templateId));
    }

    private getTv2NonGridableOptionGroup(marketType: string, mapMarketToGameTemplateIds?: MapMarketToGameTemplateIds): EventOptionGroup | undefined {
        if (mapMarketToGameTemplateIds) {
            return this.tile.event.optionGroups.find(
                (x) =>
                    (mapMarketToGameTemplateIds.marketFilterParams &&
                        mapMarketToGameTemplateIds.marketFilterParams?.every(
                            (param) => x?.parameters?.find((p) => p.key === param.key)?.value === param.value,
                        )) ||
                    x.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === marketType,
            );
        } else {
            if (this.tile.source?.optionMarketParameters?.marketTypes.some((gt) => gt === marketType)) {
                return this.tile.event.optionGroups.find(
                    (x) => x.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === marketType,
                );
            }
        }

        return;
    }

    private isMarketGridable(sportId: number, gridGroupId: string, marketType?: string): boolean {
        return this.marqueeConfig.marqueeGroupsOfMarkets.some(
            (i) => i.sportId === sportId && i.gridGroupId === gridGroupId && i.groupOfMarkets === marketType,
        );
    }

    private getNonGridableOptions(sportId: number, marketType?: string): MapMarketToGameTemplateIds | undefined {
        const templateIds = this.tile.gameTemplateIds.map((i) => Number(i));

        return this.marqueeConfig.mapMarketToGameTemplateIds.find(
            (i) => i.sportId === sportId && (i.market === marketType || i.gameTemplateIds?.some((id) => templateIds.includes(id))),
        );
    }

    // When main and fallback markets are gridable and non-gridable(tv1 or tv2)
    private getOptionGroupsForGridableAndNonGridableMarkets(eventOptions?: EventOptionGroup): EventOptionGroup | undefined {
        if (this.isMarketGridable(this.tile.sportId, this.tile.gridGroupIds[0], this.tile.marketType)) {
            eventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[0]);
            this.gridGroupId = this.tile.gridGroupIds[0];
        } else if (this.getNonGridableOptions(this.tile.sportId, this.tile.marketType)) {
            eventOptions = this.getGridableAndNonGridableOptionGroup(false);
        }
        if (!eventOptions || (this.checkOddsThreshold(eventOptions) && this.tile.fallbackMarketType !== MarketType.Unknown)) {
            this.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType] = true;
            if (this.isMarketGridable(this.tile.sportId, this.tile.gridGroupIds[0], this.tile.fallbackMarketType)) {
                eventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[0]);
                this.gridGroupId = this.tile.gridGroupIds[0];
            } else if (this.getNonGridableOptions(this.tile.sportId, this.tile.fallbackMarketType)) {
                eventOptions = this.getGridableAndNonGridableOptionGroup(false);
            }
        }

        return eventOptions;
    }

    // When main and fallback markets are gridable
    private getOptionGroupsForGridableMarkets(eventOptions?: EventOptionGroup): EventOptionGroup | undefined {
        if (this.isMarketGridable(this.tile.sportId, this.tile.gridGroupIds[0], this.tile.marketType)) {
            eventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[0]);
            this.gridGroupId = this.tile.gridGroupIds[0];
        }
        if (this.tile.marketType === MarketType.DynamicMarket) {
            eventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[0]);
            this.gridGroupId = this.tile.gridGroupIds[0];
        }

        if (!eventOptions || (this.checkOddsThreshold(eventOptions) && this.tile.fallbackMarketType !== MarketType.Unknown)) {
            this.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType] = true;
            if (this.isMarketGridable(this.tile.sportId, this.tile.gridGroupIds[1], this.tile.fallbackMarketType)) {
                eventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[1]);
                this.gridGroupId = this.tile.gridGroupIds[1];
            }
            if (this.tile.fallbackMarketType === MarketType.DynamicMarket) {
                eventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[1]);
                this.gridGroupId = this.tile.gridGroupIds[1];
            }
        }

        return eventOptions;
    }

    //When main and fallback markets are non-gridable (tv1)
    private getOptionGroupsForTv1NonGridableMarkets(eventOptions?: EventOptionGroup): EventOptionGroup | undefined {
        if (this.getNonGridableOptions(this.tile.sportId, this.tile.marketType)) {
            let nonGridableOptions = this.marqueeConfig.mapMarketToGameTemplateIds.find(
                (i) => i.sportId === this.tile.sportId && i.market === this.tile.marketType,
            );
            if (!nonGridableOptions) {
                nonGridableOptions = this.getNonGridableOptions(this.tile.sportId);
            }

            const gameTemlateIds = nonGridableOptions?.gameTemplateIds?.map(function (value) {
                return String(value);
            });
            eventOptions = this.getTv1NonGridableOptionGroup(gameTemlateIds!);
        }
        if (
            !eventOptions ||
            (this.checkOddsThreshold(eventOptions) && this.tile.fallbackMarketType !== MarketType.Unknown && !this.tile.source?.isAutomatedMarquee)
        ) {
            this.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType] = true;
            if (this.getNonGridableOptions(this.tile.sportId, this.tile.fallbackMarketType)) {
                const nonGridableOptions = this.marqueeConfig.mapMarketToGameTemplateIds.find(
                    (i) => i.sportId === this.tile.sportId && i.market === this.tile.fallbackMarketType,
                );
                const gameTemlateIds = nonGridableOptions?.gameTemplateIds?.map(function (value) {
                    return String(value);
                });
                eventOptions = this.getTv1NonGridableOptionGroup(gameTemlateIds!);
            }
        }

        return eventOptions;
    }

    //When main and fallback markets are non-gridable (tv2)
    private getOptionGroupsForTv2NonGridableMarkets(eventOptions?: EventOptionGroup): EventOptionGroup | undefined {
        if (this.getNonGridableOptions(this.tile.sportId, this.tile.marketType)) {
            const nonGridableMarketType = this.marqueeConfig.mapMarketToGameTemplateIds.find(
                (i) => i.sportId === this.tile.sportId && i.market === this.tile.marketType,
            );
            const marketType = nonGridableMarketType?.marketType;
            eventOptions = this.getTv2NonGridableOptionGroup(marketType!, nonGridableMarketType);
        }
        if (
            !eventOptions ||
            (this.checkOddsThreshold(eventOptions) && this.tile.fallbackMarketType !== MarketType.Unknown && !this.tile.source?.isAutomatedMarquee)
        ) {
            this.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType] = true;
            if (this.getNonGridableOptions(this.tile.sportId, this.tile.fallbackMarketType)) {
                const nonGridableMarketType = this.marqueeConfig.mapMarketToGameTemplateIds.find(
                    (i) => i.sportId === this.tile.sportId && i.market === this.tile.fallbackMarketType,
                );
                const marketType = nonGridableMarketType?.marketType;
                eventOptions = this.getTv2NonGridableOptionGroup(marketType!);
            }
        }

        return eventOptions;
    }

    //Fetching optiongroups for six pack event to display single market as regular marquee tile if 2 markets are closed or
    //fetching fallback market if main market (Sixpack) is closed
    private getSixpackOptionGroups(): EventOptionGroup | undefined {
        let sixpackEventOptions: EventOptionGroup | undefined;
        sixpackEventOptions = this.oddsThresholdCheckForSixpackEvents();
        if (!sixpackEventOptions) {
            if (this.tile.event.optionGroups.find((group) => group.hasGroup(this.tile.gridGroupIds[0]))) {
                this.gridGroupId = this.tile.gridGroupIds[0];
                sixpackEventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[0]);
            }
            if (!sixpackEventOptions) {
                if (this.tile.event.optionGroups.find((group) => group.hasGroup(this.tile.gridGroupIds[1]))) {
                    this.gridGroupId = this.tile.gridGroupIds[1];
                    sixpackEventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[1]);
                }
            }
            if (!sixpackEventOptions) {
                if (this.tile.event.optionGroups.find((group) => group.hasGroup(this.tile.gridGroupIds[2]))) {
                    this.gridGroupId = this.tile.gridGroupIds[2];
                    sixpackEventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[2]);
                }
            }
        }
        if (sixpackEventOptions && this.checkOddsThreshold(sixpackEventOptions)) {
            sixpackEventOptions = undefined;
        }
        if (!sixpackEventOptions && this.tile.gridGroupIds.length === 4) {
            if (this.tile.event.optionGroups.find((group) => group.hasGroup(this.tile.gridGroupIds[3]))) {
                this.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType] = true;
                this.gridGroupId = this.tile.gridGroupIds[3];
                sixpackEventOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[3]);
            }
        }
        if (!sixpackEventOptions) {
            if (
                (this.tile.gameTemplateIds && this.tile.gameTemplateIds.length > 0) ||
                (this.tile.source?.optionMarketParameters && this.tile.source?.optionMarketParameters.marketTypes.length > 0)
            ) {
                this.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType] = true;
                if (this.getNonGridableOptions(this.tile.sportId, this.tile.fallbackMarketType)) {
                    this.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType] = true;
                    sixpackEventOptions = this.getGridableAndNonGridableOptionGroup(false);
                }
            }
        }

        return sixpackEventOptions;
    }

    private oddsThresholdCheckForSixpackEvents(): EventOptionGroup | undefined {
        let sixpackEventOptions: EventOptionGroup | undefined;
        const gridGroupOneOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[0]);
        const gridGroupTwoOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[1]);
        const gridGroupThreeOptions = this.getGridableAndNonGridableOptionGroup(true, this.tile.gridGroupIds[2]);
        if (gridGroupOneOptions && gridGroupTwoOptions && gridGroupThreeOptions) {
            if (this.checkOddsThreshold(gridGroupOneOptions) && this.checkOddsThreshold(gridGroupTwoOptions)) {
                sixpackEventOptions = gridGroupThreeOptions;
            }
            if (this.checkOddsThreshold(gridGroupTwoOptions) && this.checkOddsThreshold(gridGroupThreeOptions)) {
                sixpackEventOptions = gridGroupOneOptions;
            }
            if (this.checkOddsThreshold(gridGroupOneOptions) && this.checkOddsThreshold(gridGroupThreeOptions)) {
                sixpackEventOptions = gridGroupTwoOptions;
            }
            if (
                this.checkOddsThreshold(gridGroupOneOptions) &&
                this.checkOddsThreshold(gridGroupTwoOptions) &&
                this.checkOddsThreshold(gridGroupThreeOptions)
            ) {
                sixpackEventOptions = undefined;
            }
        }

        return sixpackEventOptions;
    }

    private removeGridGroupIdsIfEmpty(tile: FixtureTile) {
        if (tile.gridGroupIds?.length === 1 && tile.gridGroupIds[0] === '') {
            tile.gridGroupIds = [];
        }
    }

    checkOddsThreshold(eventOptionGroup: EventOptionGroup): boolean {
        if (!this.marqueeConfig.isFallbackMarqueeEnabled) {
            return false;
        }
        const options = eventOptionGroup.options;
        let optionOdds: string;
        let isOddChanged = false;
        options.forEach((element) => {
            const elementOdds = element.nativePrice.odds.d;
            optionOdds = elementOdds.length > 1 ? elementOdds[0] + '.' + elementOdds[1].toString() : elementOdds[0].toString();
            if (parseFloat(optionOdds) > this.marqueeConfig.oddThresholdValue) {
                isOddChanged = true;
            }
        });

        return isOddChanged;
    }
}
